// src/pages/Catalog/ServiceOverviewPage.jsx
//
// Service Overview Page
// =============================================================================
//
// PURPOSE
// -------
// This page lists service master data in the structured Catalog area.
//
// It is intentionally separated from ServicePage.jsx. The split mirrors the
// Product area:
//
//   ServiceOverviewPage.jsx = browse / filter / quick actions
//   ServicePage.jsx         = create / edit / images / VAT / details
//
// WHY THIS SPLIT MATTERS
// ----------------------
// Service overview will likely grow with:
// - richer filters,
// - website publication state,
// - image presence indicators,
// - service package/bundle relations,
// - future WordPress sync status,
// - analytics such as most-used / not-used services.
//
// Keeping list logic separate from edit logic avoids one oversized component.
//
// URL STATE / RETURN-TO MODEL
// ---------------------------
// This page persists the current working context into the URL.
//
// Preserved state:
// - search text,
// - activity filter,
// - main category filter,
// - section filter,
// - website publication filter,
// - page,
// - pageSize.
//
// Why this matters:
// - user filters the overview,
// - opens one service,
// - checks or edits something,
// - clicks Back to overview or Cancel,
// - the exact previous overview state is restored.
//
// This follows the same pattern as VoucherOverviewPage and ProductOverviewPage.
//
// Important:
// - ServicePage should not guess where to return.
// - ServiceOverviewPage owns the overview state and passes it through
//   location.state.returnTo when navigating to create/edit pages.
//
// SERVICE MODEL DIRECTION
// -----------------------
// The backend model is expected to be:
//
//   backend/models/service.js
//   table: services
//
// Not catalog_services.
//
// LEGACY CLEANUP
// --------------
// This page assumes the target service model has been cleaned up:
//
// Removed:
// - name_internal
// - legacy price as source of truth
//
// Replaced by:
// - name_public
// - price_entry_mode
// - price_input
// - vat_rate_percent
// - price_net
// - price_vat
// - price_gross
//
// MEDIA DIRECTION
// ---------------
// Service images are handled through the generic media layer:
//
//   owner_type = "service"
//   owner_id   = service.id
//
// The service API should return images as a convenient alias:
//
//   service.images = [media_file rows...]
//
// UX ALIGNMENT
// ------------
// This overview intentionally follows the ProductOverview interaction pattern:
// - compact toolbar at the top,
// - Activity filter in the toolbar,
// - prominent Add service button,
// - horizontal filter drawer,
// - search / website / main category / section filters,
// - table-centered workflow.
//
// There is no barcode scanner here because services are not physical stock
// items.
//
// PAGINATION ARCHITECTURE
// -----------------------
// ServiceOverview uses the same stable pagination architecture as ManagePage,
// VoucherOverviewPage and ProductOverviewPage:
//
//   src/hooks/useClientPagination.js
//   src/components/common/PaginationFooter.jsx
//   src/components/common/PaginationFooter.css
//
// Important decisions:
// - useClientPagination.js stays unchanged.
// - The official returned field is `paginatedItems`.
// - Do NOT destructure alternative names such as `items`, `pageItems`,
//   `currentItems`, or `pagedItems`.
// - Pagination is NOT fixed to the viewport.
// - Pagination is a normal flex child inside `.svc-ov-tableCard`.
// - `.svc-ov-tableScroller` takes the remaining height above the footer.
// - Service totals are rendered inside the bottom PaginationFooter summary.

import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";

import {
  deleteCatalogService,
  fetchCatalogServices,
  toggleCatalogServiceActive,
} from "../../services/servicesCatalogService";

import { fetchServiceMainCategories } from "../../services/serviceMainCategoriesService";
import { fetchServiceSections } from "../../services/serviceSectionsService";

import MediaFileImage from "../../components/media/MediaFileImage";
import useClientPagination from "../../hooks/useClientPagination";
import PaginationFooter from "../../components/common/PaginationFooter";

import "./ServiceOverviewPage.css";

/* -------------------------------------------------------------------------- */
/* Small formatting helpers                                                    */
/* -------------------------------------------------------------------------- */

function asBool(v) {
  return (
    v === true ||
    String(v).trim().toLowerCase() === "true" ||
    String(v).trim() === "1"
  );
}

function money(value) {
  if (value === null || value === undefined || value === "") return "—";

  const n = Number(value);
  if (!Number.isFinite(n)) return String(value);

  return `${n.toFixed(2)} PLN`;
}

function formatVat(value) {
  if (value === null || value === undefined || value === "") return "—";

  const n = Number(value);
  if (!Number.isFinite(n)) return String(value);

  return `${n.toFixed(2)}%`;
}

function formatDuration(value, t) {
  if (value === null || value === undefined || value === "") return "—";

  const n = Number(value);
  if (!Number.isFinite(n)) return String(value);

  return t("catalog:serviceOverview.durationMinutes", { value: n });
}

function imageList(row) {
  return Array.isArray(row?.images) ? row.images : [];
}

function getPrimaryImage(row) {
  const images = imageList(row);
  if (!images.length) return null;

  return images.find((img) => asBool(img.is_primary)) || images[0];
}

function getMainCategoryName(row) {
  return (
    row?.section?.mainCategory?.name_public ||
    row?.section?.main_category?.name_public ||
    "—"
  );
}

function getSectionName(row) {
  return row?.section?.name_public || "—";
}

/**
 * Debounce hook for text search.
 *
 * Purpose:
 * - avoid backend reloads on every keystroke,
 * - keep the UI responsive,
 * - preserve the same interaction style as ProductOverview.
 */
function useDebouncedValue(value, delay = 350) {
  const [debounced, setDebounced] = useState(value);

  useEffect(() => {
    const t = setTimeout(() => setDebounced(value), delay);
    return () => clearTimeout(t);
  }, [value, delay]);

  return debounced;
}

/* -------------------------------------------------------------------------- */
/* Activity filter                                                             */
/* -------------------------------------------------------------------------- */

const ACTIVITY_ACTIVE = "active";
const ACTIVITY_INACTIVE = "inactive";
const ACTIVITY_ALL = "all";

const VALID_ACTIVITY_FILTERS = [
  ACTIVITY_ACTIVE,
  ACTIVITY_INACTIVE,
  ACTIVITY_ALL,
];

function getActivityChipLabel(activityFilter, t) {
  if (activityFilter === ACTIVITY_ACTIVE) {
    return t("catalog:serviceOverview.activity.chipActive");
  }

  if (activityFilter === ACTIVITY_INACTIVE) {
    return t("catalog:serviceOverview.activity.chipInactive");
  }

  return t("catalog:serviceOverview.activity.chipAll");
}

/* -------------------------------------------------------------------------- */
/* Pagination / storage                                                        */
/* -------------------------------------------------------------------------- */

const PAGE_SIZE_STORAGE_KEY = "svc_ov_page_size";

/* -------------------------------------------------------------------------- */
/* URL state helpers                                                           */
/* -------------------------------------------------------------------------- */

/**
 * Default filter state for ServiceOverview.
 *
 * Keep this object as the single source of truth for an empty filter state.
 *
 * It is used by:
 * - initial state,
 * - resetFilters,
 * - URL serialization.
 */
const DEFAULT_SERVICE_OVERVIEW_FILTERS = {
  q: "",
  main_category_id: "",
  section_id: "",
  publish_to_website: "",
};

const DEFAULT_SERVICE_OVERVIEW_ACTIVITY = ACTIVITY_ACTIVE;

/**
 * URL parameter names used by ServiceOverview.
 *
 * Readable names are preferred over short aliases. They make support/debugging
 * easier when a user copies a filtered URL.
 */
const URL_PARAM = {
  q: "q",
  activity: "activity",
  mainCategoryId: "mainCategoryId",
  sectionId: "sectionId",
  publishToWebsite: "publish",
  page: "page",
  pageSize: "pageSize",
};

/**
 * Read a positive integer from URLSearchParams.
 */
function readPositiveIntParam(params, name, fallback) {
  const raw = params.get(name);
  const n = Number(raw);

  if (!Number.isFinite(n) || n <= 0) {
    return fallback;
  }

  return Math.trunc(n);
}

/**
 * Build initial filter state from the current URL.
 *
 * This is called from the useState initializer so the first render already
 * receives URL state. That avoids UI flicker from "defaults first, URL later".
 */
function readServiceOverviewFiltersFromUrl(search) {
  const params = new URLSearchParams(search || "");

  return {
    ...DEFAULT_SERVICE_OVERVIEW_FILTERS,

    q: params.get(URL_PARAM.q) || "",

    main_category_id: params.get(URL_PARAM.mainCategoryId) || "",
    section_id: params.get(URL_PARAM.sectionId) || "",

    /**
     * publish_to_website is intentionally stored as:
     * - ""      => all
     * - "true"  => publish
     * - "false" => hidden
     *
     * This matches the existing select value and service API parameter style.
     */
    publish_to_website: params.get(URL_PARAM.publishToWebsite) || "",
  };
}

/**
 * Build initial activity filter from the current URL.
 *
 * If the parameter is missing or invalid, keep historical default:
 *   active
 */
function readServiceOverviewActivityFromUrl(search) {
  const params = new URLSearchParams(search || "");
  const raw = params.get(URL_PARAM.activity);

  return VALID_ACTIVITY_FILTERS.includes(raw)
    ? raw
    : DEFAULT_SERVICE_OVERVIEW_ACTIVITY;
}

/**
 * Build URLSearchParams from the current ServiceOverview state.
 *
 * Policy:
 * - default values are omitted,
 * - non-empty filters are written,
 * - page is written only when > 1,
 * - pageSize is written because users explicitly choose it.
 */
function buildServiceOverviewSearchParams({
  filters,
  activityFilter,
  page,
  pageSize,
}) {
  const params = new URLSearchParams();

  const addIfNonEmpty = (name, value) => {
    const text = String(value ?? "").trim();
    if (text) {
      params.set(name, text);
    }
  };

  addIfNonEmpty(URL_PARAM.q, filters.q);

  if (activityFilter !== DEFAULT_SERVICE_OVERVIEW_ACTIVITY) {
    params.set(URL_PARAM.activity, activityFilter);
  }

  addIfNonEmpty(URL_PARAM.mainCategoryId, filters.main_category_id);
  addIfNonEmpty(URL_PARAM.sectionId, filters.section_id);
  addIfNonEmpty(URL_PARAM.publishToWebsite, filters.publish_to_website);

  const pageNo = Number(page);
  if (Number.isFinite(pageNo) && pageNo > 1) {
    params.set(URL_PARAM.page, String(Math.trunc(pageNo)));
  }

  const pageSizeNo = Number(pageSize);
  if (Number.isFinite(pageSizeNo) && pageSizeNo > 0) {
    params.set(URL_PARAM.pageSize, String(Math.trunc(pageSizeNo)));
  }

  return params;
}

/**
 * Build a full return path for ServicePage.
 *
 * Important:
 * This helper builds returnTo from the current React state instead of simply
 * returning location.search.
 *
 * Why:
 * - URL synchronization happens in useEffect,
 * - useEffect may run after the latest state change,
 * - the user can type a filter and immediately click Edit,
 * - using React state avoids returning a stale query string.
 */
function buildServiceOverviewReturnToPath({
  pathname,
  filters,
  activityFilter,
  page,
  pageSize,
}) {
  const params = buildServiceOverviewSearchParams({
    filters,
    activityFilter,
    page,
    pageSize,
  });

  const search = params.toString();
  return search ? `${pathname}?${search}` : pathname;
}

/* -------------------------------------------------------------------------- */
/* Main component                                                              */
/* -------------------------------------------------------------------------- */

export default function ServiceOverviewPage() {
  const { t } = useTranslation(["catalog", "common"]);
  const navigate = useNavigate();
  const location = useLocation();
  const quickSearchRef = useRef(null);

  /* ------------------------------------------------------------------------ */
  /* Data state                                                                */
  /* ------------------------------------------------------------------------ */

  const [rows, setRows] = useState([]);
  const [mainCategories, setMainCategories] = useState([]);
  const [sections, setSections] = useState([]);

  const [loading, setLoading] = useState(false);
  const [actionId, setActionId] = useState(null);

  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");

  /* ------------------------------------------------------------------------ */
  /* Filter state                                                              */
  /* ------------------------------------------------------------------------ */

  const [filtersOpen, setFiltersOpen] = useState(true);

  /**
   * Activity and filter state are initialized from URL.
   *
   * This is what makes returning from ServicePage preserve the previous
   * overview context.
   */
  const [activityFilter, setActivityFilter] = useState(() =>
    readServiceOverviewActivityFromUrl(window.location.search)
  );

  const [filters, setFilters] = useState(() =>
    readServiceOverviewFiltersFromUrl(window.location.search)
  );

  const debouncedSearch = useDebouncedValue(filters.q, 350);

  /* ------------------------------------------------------------------------ */
  /* Derived dictionaries / filter display state                               */
  /* ------------------------------------------------------------------------ */

  const visibleSections = useMemo(() => {
    if (!filters.main_category_id) return sections;

    return sections.filter(
      (s) => String(s.main_category_id) === String(filters.main_category_id)
    );
  }, [sections, filters.main_category_id]);

  const activityChipLabel = useMemo(
    () => getActivityChipLabel(activityFilter, t),
    [activityFilter, t]
  );

  const hasActiveFilters = useMemo(() => {
    return (
      Boolean(filters.q.trim()) ||
      Boolean(filters.main_category_id) ||
      Boolean(filters.section_id) ||
      filters.publish_to_website !== "" ||
      activityFilter !== ACTIVITY_ACTIVE
    );
  }, [filters, activityFilter]);

  /* ------------------------------------------------------------------------ */
  /* Shared pagination                                                         */
  /* ------------------------------------------------------------------------ */

  /**
   * Initial page size is URL-aware.
   *
   * Priority:
   * 1) URL pageSize,
   * 2) previously saved local preference,
   * 3) default 50.
   */
  const initialPageSize = useMemo(() => {
    const params = new URLSearchParams(window.location.search || "");
    const fromUrl = readPositiveIntParam(params, URL_PARAM.pageSize, null);

    if (fromUrl) {
      return fromUrl;
    }

    const saved = Number(localStorage.getItem(PAGE_SIZE_STORAGE_KEY));
    return Number.isFinite(saved) && saved > 0 ? saved : 50;
  }, []);

  /**
   * Initial page number from URL.
   *
   * useClientPagination receives initialPageSize only, so we restore the page
   * after the hook has created its internal state.
   */
  const initialPageFromUrl = useMemo(() => {
    const params = new URLSearchParams(window.location.search || "");
    return readPositiveIntParam(params, URL_PARAM.page, 1);
  }, []);

  const {
    page,
    pageSize,
    pageCount: totalPages,
    totalRows,
    paginatedItems: paginatedRows,
    setPage,
    setPageSize,
    goPrev,
    goNext,
  } = useClientPagination(rows, {
    initialPageSize,
  });

  /**
   * Restore initial page from URL once.
   *
   * We keep useClientPagination unchanged and perform this page restoration here.
   */
  const didRestoreInitialPageRef = useRef(false);

  useEffect(() => {
    if (didRestoreInitialPageRef.current) return;

    didRestoreInitialPageRef.current = true;

    if (initialPageFromUrl > 1) {
      setPage(initialPageFromUrl);
    }
  }, [initialPageFromUrl, setPage]);

  /**
   * Keep browser URL aligned with current overview state.
   *
   * replace: true prevents typing in search/filter fields from creating dozens
   * of browser history entries.
   */
  useEffect(() => {
    const params = buildServiceOverviewSearchParams({
      filters,
      activityFilter,
      page,
      pageSize,
    });

    const nextSearch = params.toString();
    const currentSearch = String(location.search || "").replace(/^\?/, "");

    if (nextSearch === currentSearch) {
      return;
    }

    navigate(
      {
        pathname: location.pathname,
        search: nextSearch ? `?${nextSearch}` : "",
      },
      { replace: true }
    );
  }, [
    filters,
    activityFilter,
    page,
    pageSize,
    location.pathname,
    location.search,
    navigate,
  ]);

  const handlePageSizeChange = useCallback(
    (nextPageSize) => {
      setPageSize(nextPageSize);

      try {
        localStorage.setItem(PAGE_SIZE_STORAGE_KEY, String(nextPageSize));
      } catch {
        // Ignore storage errors, for example private mode or quota issues.
      }
    },
    [setPageSize]
  );

  /* ------------------------------------------------------------------------ */
  /* Footer summary                                                            */
  /* ------------------------------------------------------------------------ */

  const summary = useMemo(() => {
    const acc = {
      total: rows.length,
      active: 0,
      inactive: 0,
      published: 0,
      withImage: 0,
    };

    for (const row of rows) {
      if (asBool(row.is_active)) acc.active += 1;
      else acc.inactive += 1;

      if (asBool(row.publish_to_website)) acc.published += 1;
      if (imageList(row).length > 0) acc.withImage += 1;
    }

    return acc;
  }, [rows]);

  /* ------------------------------------------------------------------------ */
  /* Filter helpers                                                            */
  /* ------------------------------------------------------------------------ */

  const setFilter = useCallback(
    (name, value) => {
      setPage(1);

      setFilters((prev) => {
        const next = { ...prev, [name]: value };

        /**
         * If main category changes, reset section filter because the previously
         * selected section may belong to another category.
         */
        if (name === "main_category_id") {
          next.section_id = "";
        }

        return next;
      });
    },
    [setPage]
  );

  const setActivity = useCallback(
    (nextActivity) => {
      setPage(1);
      setActivityFilter(nextActivity);
    },
    [setPage]
  );

  const resetFilters = useCallback(() => {
    setPage(1);

    setActivityFilter(DEFAULT_SERVICE_OVERVIEW_ACTIVITY);

    setFilters({
      ...DEFAULT_SERVICE_OVERVIEW_FILTERS,
    });

    setError("");
    setSuccess("");
  }, [setPage]);

  const buildQueryParams = useCallback(() => {
    const params = {};

    /**
     * Activity filter is intentionally in the toolbar, not in the drawer.
     * This mirrors ProductOverview and keeps "what am I looking at?" visible
     * even when filters are collapsed.
     */
    if (activityFilter === ACTIVITY_ACTIVE) {
      params.active = true;
    }

    if (activityFilter === ACTIVITY_INACTIVE) {
      params.active = false;
    }

    if (debouncedSearch.trim()) {
      params.q = debouncedSearch.trim();
    }

    if (filters.section_id) {
      params.section_id = filters.section_id;
    }

    if (filters.publish_to_website !== "") {
      params.publish_to_website = filters.publish_to_website;
    }

    /**
     * Backend supports main_category_id in listServices.
     * Keeping this here makes the filter efficient server-side.
     */
    if (filters.main_category_id && !filters.section_id) {
      params.main_category_id = filters.main_category_id;
    }

    return params;
  }, [
    activityFilter,
    debouncedSearch,
    filters.section_id,
    filters.publish_to_website,
    filters.main_category_id,
  ]);

  /* ------------------------------------------------------------------------ */
  /* Loading                                                                   */
  /* ------------------------------------------------------------------------ */

  const loadDictionaries = useCallback(async () => {
    const [cats, secs] = await Promise.all([
      fetchServiceMainCategories({ active: true }),
      fetchServiceSections({ active: true }),
    ]);

    setMainCategories(Array.isArray(cats) ? cats : []);
    setSections(Array.isArray(secs) ? secs : []);
  }, []);

  const loadRows = useCallback(async () => {
    setLoading(true);
    setError("");

    try {
      const data = await fetchCatalogServices(buildQueryParams());
      let list = Array.isArray(data) ? data : [];

      /**
       * Defensive fallback:
       * if backend is temporarily behind and does not filter main_category_id,
       * the frontend still narrows rows correctly.
       */
      if (filters.main_category_id && !filters.section_id) {
        list = list.filter((row) => {
          const rowMainCategoryId =
            row.section?.main_category_id || row.section?.mainCategory?.id || "";

          return String(rowMainCategoryId) === String(filters.main_category_id);
        });
      }

      setRows(list);
    } catch (e) {
      setError(e?.message || t("catalog:serviceOverview.messages.loadFailed"));
    } finally {
      setLoading(false);
    }
  }, [buildQueryParams, filters.main_category_id, filters.section_id, t]);

  useEffect(() => {
    (async () => {
      try {
        await loadDictionaries();
      } catch (e) {
        setError(e?.message || t("catalog:serviceOverview.messages.initializeFailed"));
      }
    })();
  }, [loadDictionaries, t]);

  useEffect(() => {
    loadRows();
  }, [loadRows]);

  /**
   * Keyboard shortcut:
   * "/" focuses quick search, same UX as Voucher/Product overviews.
   */
  useEffect(() => {
    const onKeyDown = (e) => {
      if (
        e.key === "/" &&
        !e.ctrlKey &&
        !e.altKey &&
        !e.metaKey &&
        !/^(INPUT|SELECT|TEXTAREA)$/.test(
          document.activeElement?.tagName || ""
        )
      ) {
        e.preventDefault();
        quickSearchRef.current?.focus();
      }
    };

    window.addEventListener("keydown", onKeyDown);
    return () => window.removeEventListener("keydown", onKeyDown);
  }, []);

  /**
   * Auto-hide success messages after a few seconds.
   * Errors remain visible until the next action/filter reload.
   */
  useEffect(() => {
    if (!success) return;

    const id = setTimeout(() => setSuccess(""), 4500);
    return () => clearTimeout(id);
  }, [success]);

  /* ------------------------------------------------------------------------ */
  /* Return-to navigation                                                      */
  /* ------------------------------------------------------------------------ */

  /**
   * Build a stable return target for ServicePage.
   *
   * This preserves:
   * - filters,
   * - activity,
   * - page,
   * - pageSize.
   *
   * We intentionally build it from current React state, not from location.search,
   * because location.search is updated asynchronously through useEffect.
   */
  const buildReturnToPath = useCallback(() => {
    return buildServiceOverviewReturnToPath({
      pathname: location.pathname,
      filters,
      activityFilter,
      page,
      pageSize,
    });
  }, [location.pathname, filters, activityFilter, page, pageSize]);

  /* ------------------------------------------------------------------------ */
  /* Actions                                                                   */
  /* ------------------------------------------------------------------------ */

  const handleAdd = useCallback(() => {
    navigate("/catalog/services/new", {
      state: {
        /**
         * ServicePage will use this for Back to overview and Cancel.
         *
         * This makes create mode behave consistently when the user started
         * from a filtered overview.
         */
        returnTo: buildReturnToPath(),
      },
    });
  }, [navigate, buildReturnToPath]);

  const handleEdit = useCallback(
    (row) => {
      navigate(`/catalog/services/${row.id}`, {
        state: {
          service: row,
          phase: "edit",

          /**
           * ServicePage should return to the exact current overview state.
           */
          returnTo: buildReturnToPath(),
        },
      });
    },
    [navigate, buildReturnToPath]
  );

  const handleToggleActive = useCallback(
    async (row) => {
      setActionId(row.id);
      setError("");
      setSuccess("");

      try {
        await toggleCatalogServiceActive(row.id, !asBool(row.is_active));
        setSuccess(
          asBool(row.is_active)
            ? t("catalog:serviceOverview.messages.deactivated")
            : t("catalog:serviceOverview.messages.activated")
        );

        await loadRows();
      } catch (e) {
        setError(e?.message || t("catalog:serviceOverview.messages.changeActiveFailed"));
      } finally {
        setActionId(null);
      }
    },
    [loadRows, t]
  );

  const handleDelete = useCallback(
    async (row) => {
      const ok = window.confirm(
        t("catalog:serviceOverview.confirmDelete", {
          label: row.name_public,
        })
      );

      if (!ok) return;

      setActionId(row.id);
      setError("");
      setSuccess("");

      try {
        await deleteCatalogService(row.id);
        setSuccess(t("catalog:serviceOverview.messages.deleted"));
        await loadRows();
      } catch (e) {
        setError(e?.message || t("catalog:serviceOverview.messages.deleteFailed"));
      } finally {
        setActionId(null);
      }
    },
    [loadRows, t]
  );

  /**
   * Placeholder handlers for visual parity with ProductOverview.
   *
   * ServiceOverview currently does not implement resizable columns yet.
   * Keeping these actions as safe no-ops preserves toolbar alignment and makes
   * it easy to wire in the same column-width model later if needed.
   */
  const autoFitAll = useCallback(() => {
    // Intentionally reserved for future column width support.
  }, []);

  const resetAllWidths = useCallback(() => {
    // Intentionally reserved for future column width support.
  }, []);

  /* ------------------------------------------------------------------------ */
  /* Render                                                                    */
  /* ------------------------------------------------------------------------ */

  return (
    <div className="svc-overview" role="main" aria-label={t("catalog:serviceOverview.aria.main")}>
      {/* Toolbar */}
      <div className="svc-ov-toolbar">
        <div className="svc-ov-titleBlock">
          <h1>{t("catalog:serviceOverview.title")}</h1>
        </div>

        <div
          className="svc-ov-toolbar__actions"
          role="group"
          aria-label={t("catalog:serviceOverview.aria.toolbar")}
        >
          <div
            className="svc-ov-activity"
            role="group"
            aria-label={t("catalog:serviceOverview.aria.activityFilter")}
          >
            <span className="svc-ov-activity__label">{t("catalog:serviceOverview.activity.label")}</span>

            <label
              className={[
                "svc-ov-activityPill",
                activityFilter === ACTIVITY_ACTIVE ? "is-active" : "",
              ]
                .filter(Boolean)
                .join(" ")}
            >
              <input
                type="radio"
                name="serviceActivity"
                checked={activityFilter === ACTIVITY_ACTIVE}
                onChange={() => setActivity(ACTIVITY_ACTIVE)}
              />
              <span>{t("catalog:serviceOverview.activity.active")}</span>
            </label>

            <label
              className={[
                "svc-ov-activityPill",
                activityFilter === ACTIVITY_INACTIVE ? "is-inactive" : "",
              ]
                .filter(Boolean)
                .join(" ")}
            >
              <input
                type="radio"
                name="serviceActivity"
                checked={activityFilter === ACTIVITY_INACTIVE}
                onChange={() => setActivity(ACTIVITY_INACTIVE)}
              />
              <span>{t("catalog:serviceOverview.activity.inactive")}</span>
            </label>

            <button
              type="button"
              className={[
                "svc-ov-activityAll",
                activityFilter === ACTIVITY_ALL ? "is-active" : "",
              ]
                .filter(Boolean)
                .join(" ")}
              onClick={() => setActivity(ACTIVITY_ALL)}
              title={t("catalog:serviceOverview.titles.showAllServices")}
            >
              {t("catalog:serviceOverview.activity.all")}
            </button>
          </div>

          <span
            className="svc-ov-statusChip"
            aria-label={t("catalog:serviceOverview.aria.currentActivityFilter")}
          >
            {activityChipLabel}
          </span>

          <button
            type="button"
            className="svc-ov-btn svc-ov-btn--primary svc-ov-btn--add"
            onClick={handleAdd}
          >
            <span className="svc-ov-btnPlus" aria-hidden="true">
              +
            </span>
            {t("catalog:serviceOverview.actions.addService")}
          </button>

          <button
            type="button"
            className="svc-ov-btn svc-ov-btn--secondary"
            onClick={() => setFiltersOpen((v) => !v)}
            aria-expanded={filtersOpen}
            aria-controls="service-overview-filters"
          >
            {filtersOpen
              ? t("catalog:serviceOverview.actions.hideFilters")
              : t("catalog:serviceOverview.actions.showFilters")}
          </button>

          <button
            type="button"
            className="svc-ov-btn svc-ov-btn--ghost"
            onClick={resetFilters}
            disabled={!hasActiveFilters}
            title={t("catalog:serviceOverview.titles.resetFilters")}
          >
            {t("catalog:serviceOverview.actions.reset")}
          </button>

          <button
            type="button"
            className="svc-ov-btn svc-ov-btn--secondary"
            onClick={autoFitAll}
            title={t("catalog:serviceOverview.titles.autoFitColumnsReserved")}
          >
            {t("catalog:serviceOverview.actions.autoFitColumns")}
          </button>

          <button
            type="button"
            className="svc-ov-btn svc-ov-btn--ghost"
            onClick={resetAllWidths}
            title={t("catalog:serviceOverview.titles.resetWidthsReserved")}
          >
            {t("catalog:serviceOverview.actions.resetWidths")}
          </button>
        </div>
      </div>

      {/* Filters drawer */}
      {filtersOpen ? (
        <section
          className="svc-ov-filters"
          id="service-overview-filters"
          aria-label={t("catalog:serviceOverview.aria.filters")}
        >
          <div className="svc-ov-filtersGrid">
            <div className="svc-ov-filterGroup svc-ov-filterSearch">
              <label htmlFor="svcOvSearch">{t("catalog:serviceOverview.filters.searchService")}</label>

              <input
                ref={quickSearchRef}
                id="svcOvSearch"
                type="text"
                value={filters.q}
                onChange={(e) => setFilter("q", e.target.value)}
                placeholder={t("catalog:serviceOverview.filters.searchPlaceholder")}
                autoComplete="off"
                inputMode="search"
              />

              <p className="svc-ov-fieldHelp">
                {t("catalog:serviceOverview.filters.quickSearchHint")}
              </p>
            </div>

            <div className="svc-ov-filterGroup svc-ov-filterWebsite">
              <label htmlFor="svcOvWebsite">{t("catalog:serviceOverview.filters.website")}</label>

              <select
                id="svcOvWebsite"
                value={filters.publish_to_website}
                onChange={(e) =>
                  setFilter("publish_to_website", e.target.value)
                }
              >
                <option value="">{t("catalog:serviceOverview.filters.allWebsiteFlags")}</option>
                <option value="true">{t("catalog:serviceOverview.filters.publish")}</option>
                <option value="false">{t("catalog:serviceOverview.filters.hidden")}</option>
              </select>
            </div>

            <div className="svc-ov-filterGroup svc-ov-filterMainCategory">
              <div className="svc-ov-filterGroupHeader">
                <label>{t("catalog:serviceOverview.filters.mainCategories")}</label>

                {filters.main_category_id ? (
                  <button
                    type="button"
                    className="svc-ov-miniClear"
                    onClick={() => setFilter("main_category_id", "")}
                  >
                    {t("catalog:serviceOverview.actions.clear")}
                  </button>
                ) : null}
              </div>

              <div
                className="svc-ov-pillList"
                role="radiogroup"
                aria-label={t("catalog:serviceOverview.aria.mainCategoryFilter")}
              >
                {mainCategories.length === 0 ? (
                  <span className="svc-ov-emptyFilterHint">{t("catalog:serviceOverview.empty.noCategories")}</span>
                ) : (
                  mainCategories.map((cat) => (
                    <label key={cat.id} className="svc-ov-filterPill">
                      <input
                        type="radio"
                        name="serviceMainCategoryFilter"
                        checked={
                          String(filters.main_category_id) === String(cat.id)
                        }
                        onChange={() =>
                          setFilter("main_category_id", String(cat.id))
                        }
                      />
                      <span>{cat.name_public}</span>
                    </label>
                  ))
                )}
              </div>
            </div>

            <div className="svc-ov-filterGroup svc-ov-filterSection">
              <div className="svc-ov-filterGroupHeader">
                <label>{t("catalog:serviceOverview.filters.sections")}</label>

                {filters.section_id ? (
                  <button
                    type="button"
                    className="svc-ov-miniClear"
                    onClick={() => setFilter("section_id", "")}
                  >
                    {t("catalog:serviceOverview.actions.clear")}
                  </button>
                ) : null}
              </div>

              <div
                className="svc-ov-pillList"
                role="radiogroup"
                aria-label={t("catalog:serviceOverview.aria.sectionFilter")}
              >
                {visibleSections.length === 0 ? (
                  <span className="svc-ov-emptyFilterHint">{t("catalog:serviceOverview.empty.noSections")}</span>
                ) : (
                  visibleSections.map((section) => (
                    <label key={section.id} className="svc-ov-filterPill">
                      <input
                        type="radio"
                        name="serviceSectionFilter"
                        checked={String(filters.section_id) === String(section.id)}
                        onChange={() => setFilter("section_id", String(section.id))}
                      />
                      <span>{section.name_public}</span>
                    </label>
                  ))
                )}
              </div>
            </div>
          </div>
        </section>
      ) : null}

      {error ? (
        <div className="svc-ov-alert svc-ov-alert--error" role="alert">
          {error}
        </div>
      ) : null}

      {success ? (
        <div className="svc-ov-alert svc-ov-alert--success" role="status">
          {success}
        </div>
      ) : null}

      {/* Table + shared pagination */}
      <section className="svc-ov-tableCard" aria-label={t("catalog:serviceOverview.aria.table")}>
        <div className="svc-ov-tableScroller" aria-busy={loading}>
          <table className="svc-ov-table">
            <thead>
              <tr>
                <th className="svc-left">{t("catalog:serviceOverview.columns.image")}</th>
                <th className="svc-left">{t("catalog:serviceOverview.columns.mainCategory")}</th>
                <th className="svc-left">{t("catalog:serviceOverview.columns.section")}</th>
                <th className="svc-left">{t("catalog:serviceOverview.columns.service")}</th>
                <th className="svc-left">{t("catalog:serviceOverview.columns.duration")}</th>
                <th className="svc-left">{t("catalog:serviceOverview.columns.grossPrice")}</th>
                <th className="svc-left">{t("catalog:serviceOverview.columns.vat")}</th>
                <th className="svc-left">{t("catalog:serviceOverview.columns.active")}</th>
                <th className="svc-left">{t("catalog:serviceOverview.columns.website")}</th>
                <th className="svc-right">{t("catalog:serviceOverview.columns.actions")}</th>
              </tr>
            </thead>

            <tbody>
              {loading ? (
                <tr>
                  <td colSpan="10" className="svc-ov-empty">
                    {t("catalog:serviceOverview.messages.loading")}
                  </td>
                </tr>
              ) : null}

              {!loading && rows.length === 0 ? (
                <tr>
                  <td colSpan="10" className="svc-ov-empty">
                    {t("catalog:serviceOverview.empty.noServices")}
                  </td>
                </tr>
              ) : null}

              {!loading &&
                paginatedRows.map((row) => {
                  const primaryImage = getPrimaryImage(row);
                  const disabled = actionId === row.id;
                  const active = asBool(row.is_active);
                  const published = asBool(row.publish_to_website);

                  return (
                    <tr
                      key={row.id}
                      className={!active ? "svc-ov-rowMuted" : ""}
                    >
                      <td className="svc-left">
                        {primaryImage ? (
                          <MediaFileImage
                            media={primaryImage}
                            className="svc-ov-thumb"
                            fallbackClassName="svc-ov-thumb svc-ov-thumb--empty"
                            alt={row.name_public || t("catalog:serviceOverview.media.serviceImageAlt")}
                            emptyText={t("catalog:serviceOverview.media.noImage")}
                            loadingText="..."
                            failedText={t("catalog:serviceOverview.media.image")}
                          />
                        ) : (
                          <div className="svc-ov-thumb svc-ov-thumb--empty">
                            —
                          </div>
                        )}
                      </td>

                      <td className="svc-left">{getMainCategoryName(row)}</td>

                      <td className="svc-left">{getSectionName(row)}</td>

                      <td className="svc-left">
                        <div className="svc-ov-nameCell">
                          <strong>{row.name_public}</strong>

                          {row.description_short ? (
                            <small>{row.description_short}</small>
                          ) : null}

                          {row.price_label ? <span>{row.price_label}</span> : null}
                        </div>
                      </td>

                      <td className="svc-left">
                        {formatDuration(row.duration_minutes, t)}
                      </td>

                      <td className="svc-left">{money(row.price_gross)}</td>

                      <td className="svc-left">
                        {formatVat(row.vat_rate_percent)}
                      </td>

                      <td className="svc-left">
                        <span
                          className={
                            active
                              ? "svc-ov-chip svc-ov-chip--green"
                              : "svc-ov-chip"
                          }
                        >
                          {active
                            ? t("catalog:serviceOverview.activity.active")
                            : t("catalog:serviceOverview.activity.inactive")}
                        </span>
                      </td>

                      <td className="svc-left">
                        <span
                          className={
                            published
                              ? "svc-ov-chip svc-ov-chip--blue"
                              : "svc-ov-chip"
                          }
                        >
                          {published
                            ? t("catalog:serviceOverview.status.publish")
                            : t("catalog:serviceOverview.status.hidden")}
                        </span>
                      </td>

                      <td className="svc-right">
                        <div className="svc-ov-actions">
                          <button
                            type="button"
                            className="svc-ov-linkBtn"
                            onClick={() => handleEdit(row)}
                            disabled={disabled}
                          >
                            {t("catalog:serviceOverview.actions.edit")}
                          </button>

                          <span className="svc-ov-sep">•</span>

                          <button
                            type="button"
                            className="svc-ov-linkBtn svc-ov-linkBtn--warning"
                            onClick={() => handleToggleActive(row)}
                            disabled={disabled}
                          >
                            {active
                              ? t("catalog:serviceOverview.actions.deactivate")
                              : t("catalog:serviceOverview.actions.activate")}
                          </button>

                          <span className="svc-ov-sep">•</span>

                          <button
                            type="button"
                            className="svc-ov-linkBtn svc-ov-linkBtn--danger"
                            onClick={() => handleDelete(row)}
                            disabled={disabled}
                          >
                            {t("catalog:serviceOverview.actions.delete")}
                          </button>
                        </div>
                      </td>
                    </tr>
                  );
                })}
            </tbody>
          </table>
        </div>

        <PaginationFooter
          className="svc-ov-pagination"
          page={page}
          pageCount={totalPages}
          pageSize={pageSize}
          pageSizeOptions={[10, 25, 50, 100]}
          totalRows={totalRows}
          totalLabel={t("catalog:serviceOverview.totalLabel", { count: totalRows })}
          onPageSizeChange={handlePageSizeChange}
          onPrev={goPrev}
          onNext={goNext}
          summary={
            <div className="svc-ov-paginationSummary" aria-label={t("catalog:serviceOverview.aria.totals")}>
              <span className="svc-ov-totalChip">
                <em>{t("catalog:serviceOverview.totals.total")}</em>
                <strong>{summary.total}</strong>
              </span>
              <span className="svc-ov-totalChip">
                <em>{t("catalog:serviceOverview.totals.active")}</em>
                <strong>{summary.active}</strong>
              </span>
              <span className="svc-ov-totalChip">
                <em>{t("catalog:serviceOverview.totals.published")}</em>
                <strong>{summary.published}</strong>
              </span>
              <span className="svc-ov-totalChip">
                <em>{t("catalog:serviceOverview.totals.images")}</em>
                <strong>{summary.withImage}</strong>
              </span>
            </div>
          }
        />
      </section>
    </div>
  );
}

